import moment from 'moment'
import { Fragment, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EventCalendar } from '@/types/Calendar'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import ReleaseDateField from '../../../components/ReleaseDateField/releaseDateField'
import { DateSelection } from '../../../models/dateSelection'
import { DAYTIME_FORMAT } from '../../../models/constants'
import { AnyBlockLine, AnyFormBlock } from '../../constants/blockByType'
import { useOccasionalSlotForm } from '../../hooks/useOccasionalSlotForm'
import SlotTimeFields from '../SlotTimeFields/slotTimeFields'
import DateSelectFields from '../DateSelectFields/dateSelectFields'
import SlotPriorityField from '../SlotPriorityField/slotPriorityField'
import ExpiranceField from '../ExpiranceField/expiranceField'
import SlotNoteField from '../SlotNoteField/slotNoteField'
import SlotCountField from '../SlotCountField/slotCountField'
import SlotPlaceField from '../SlotPlaceField/slotPlaceField'
import styles from '../../occasionalSlotForm.module.scss'

interface OccasionalSlotFieldsProps {
    form: ReturnType<typeof useOccasionalSlotForm>
    lines: AnyBlockLine[]
    calendarEvents: EventCalendar[]
    places: OccasionalSlotsPlaces[]
}

export default function OccasionalSlotFields({ form, lines, calendarEvents, places }: OccasionalSlotFieldsProps) {
    const { t } = useTranslation()

    const blockRenderers: Record<AnyFormBlock, () => ReactNode> = {
        dateSelection: () => (
            <DateSelectFields
                dates={form.dateSelection}
                setDates={(dates: DateSelection) => {
                    form.setDateSelection(dates)
                    form.clearErrors('date')
                }}
                calendarEvents={calendarEvents}
                error={form.errors.date}
            />
        ),
        timeSelection: () => (
            <SlotTimeFields
                startDate={form.firstDate}
                startTime={form.startTime}
                setStartTime={(value: string) => {
                    form.setStartTime(value)
                    form.clearErrors('time')
                }}
                endTime={form.endTime}
                setEndTime={(value: string) => {
                    form.setEndTime(value)
                    form.clearErrors('time')
                }}
                error={form.errors.time}
            />
        ),
        slotCount: () => (
            <SlotCountField
                value={form.occasionalSlotNumber}
                onChange={form.handleSlotCountChange}
                max={100}
                error={form.errors.count || form.errors.children}
            />
        ),
        sectionSelection: () => (
            <SelectUnique
                selectOptions={form.selectOptionsSection}
                selectValue={form.selectedSectionId}
                setSelectValue={form.handleSelectedSection}
                error={form.errors.section}
                formClassName={styles.UserInput}
                label={form.isRpeOrLaep ? t('sector') : t('section')}
            />
        ),
        prioritySelection: () =>
            form.placesStillAvailable && (
                <SlotPriorityField
                    groups={form.currentDaycare.groups ?? []}
                    childOptions={form.childGroupsForSelect}
                    groupIds={form.priorityGroupIds}
                    childIds={form.priorityChildIds}
                    setGroupIds={form.setPriorityGroupIds}
                    setChildIds={form.setPriorityChildIds}
                />
            ),
        confirmedChildrenSelection: () => (
            <SelectMultiple
                selectOptions={form.selectOptionsConfirmChild}
                selectValues={form.confirmedChildIds}
                setSelectValues={(values: number[]) => {
                    form.setConfirmedChildIds(values)
                    form.clearErrors('children')
                }}
                label={t('childConfirmed')}
                maxSelected={Number(form.occasionalSlotNumber)}
                error={form.errors.children}
            />
        ),
        expirationSelection: () =>
            form.placesStillAvailable && (
                <ExpiranceField
                    expirance={form.expiration}
                    setExpirance={form.setExpiration}
                    minDate={moment().format(DAYTIME_FORMAT)}
                    maxDate={form.fullStartDate}
                />
            ),
        scheduleSelection: () =>
            form.canSchedule && (
                <ReleaseDateField
                    value={form.schedule}
                    onChange={form.setSchedule}
                    maxDate={form.fullStartDate}
                    minDate={moment().format(DAYTIME_FORMAT)}
                    className={styles.UserInput}
                />
            ),
        rpePlacesSelection: () =>
            form.isRpeOrLaep && <SlotPlaceField places={places} value={form.placeId} onChange={form.setPlaceId} />,
        placesSelection: () =>
            !form.isRpeOrLaep && <SlotPlaceField places={places} value={form.placeId} onChange={form.setPlaceId} />,
        internalNoteField: () => (
            <SlotNoteField
                label={t('internalNote')}
                value={form.internalNoteValue}
                onChange={form.setInternalNoteValue}
            />
        ),
        externalNoteField: () => (
            <SlotNoteField
                label={t('externalNote')}
                value={form.externalNoteValue}
                onChange={form.setExternalNoteValue}
                iconSelection={{ value: form.externalIcon, onChange: form.setExternalIcon }}
            />
        )
    }

    return lines.map((line: AnyBlockLine): ReactNode => {
        const visibleBlocks: { block: AnyFormBlock; content: ReactNode }[] = line
            .map((block: AnyFormBlock) => ({ block, content: blockRenderers[block]() }))
            .filter(({ content }) => Boolean(content))
        if (visibleBlocks.length === 0) return null
        return (
            <div
                key={line.join('-')}
                className={styles.Row}
                ref={(element) => visibleBlocks.forEach(({ block }) => form.registerBlock(block)(element))}
            >
                {visibleBlocks.map(({ block, content }) => (
                    <Fragment key={block}>{content}</Fragment>
                ))}
            </div>
        )
    })
}
